/**
 * Server-sent events for the workspace. Three rules that matter operationally: the stream is an *invalidation
 * signal*, not the source of truth (a page still reads from the REST endpoints); a connection the browser cannot
 * hold degrades into an explicit poll rather than a frozen chart that looks like a quiet plant; and the
 * browser's own `Last-Event-ID` is what makes a reconnect replay the events it missed, which the server answers
 * from the persisted stream ledger. The escalation and recovery rules themselves live in `stream-resilience.ts`,
 * where they can be tested without a browser.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { POLL_INTERVAL_MS, initialStream, onFailed, onOpened, shouldPoll, type StreamPhase, type StreamResilience } from './stream-resilience.js';

/**
 * The server owns this vocabulary — it is the same list `STREAM_TOPICS` in
 * `server/src/domain/contracts.ts`, mirrored because the two packages do not share build output. A topic that
 * exists only here would silently never fire, and a topic the server publishes that is missing here is dropped
 * on the floor; `web/test/stream-topics.test.ts` compares the two lists so drift fails a test instead.
 */
export const STREAM_TOPICS = ['telemetry.tick', 'regime.change', 'anomaly.opened', 'anomaly.updated', 'dispatch.state', 'dataquality', 'entitlement', 'prognosis.updated'] as const;
export type StreamTopic = (typeof STREAM_TOPICS)[number];

export interface StreamMessage<T = Record<string, unknown>> {
  id: number;
  topic: StreamTopic;
  payload: T;
}

export type StreamState = StreamPhase;

interface Options {
  topics?: StreamTopic[];
  onMessage?: (message: StreamMessage) => void;
  /**
   * Called every `POLL_INTERVAL_MS` while the stream is unusable, and only then. A page's refetcher is the right
   * thing to hand it: the stream never carries data, so the fallback is not a second transport but the same
   * invalidation, delivered late and labelled.
   */
  onPoll?: () => void;
  enabled?: boolean;
}

export function useLiveStream(options: Options = {}): { state: StreamState; count: number; last: StreamMessage | null; subscribe: (topic: StreamTopic, handler: (message: StreamMessage) => void) => () => void } {
  const { topics, enabled = true } = options;
  const [state, setState] = useState<StreamState>('connecting');
  const [count, setCount] = useState(0);
  const [last, setLast] = useState<StreamMessage | null>(null);
  const source = useRef<EventSource | null>(null);
  const handlers = useRef(new Map<StreamTopic, Set<(message: StreamMessage) => void>>());
  const onMessage = useRef(options.onMessage);
  onMessage.current = options.onMessage;
  const onPoll = useRef(options.onPoll);
  onPoll.current = options.onPoll;

  const subscribe = useCallback((topic: StreamTopic, handler: (message: StreamMessage) => void) => {
    const set = handlers.current.get(topic) ?? new Set();
    set.add(handler);
    handlers.current.set(topic, set);
    return () => {
      set.delete(handler);
    };
  }, []);

  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') {
      setState('closed');
      return;
    }
    const es = new EventSource('/api/v1/stream', { withCredentials: true });
    source.current = es;
    let resilience: StreamResilience = initialStream();
    let poll: ReturnType<typeof setInterval> | null = null;
    const transition = (next: StreamResilience): void => {
      resilience = next;
      setState(next.phase);
      if (shouldPoll(next) && poll === null) poll = setInterval(() => onPoll.current?.(), POLL_INTERVAL_MS);
      if (!shouldPoll(next) && poll !== null) {
        clearInterval(poll);
        poll = null;
      }
    };
    es.onopen = () => transition(onOpened(resilience));
    es.onerror = () => transition(onFailed(resilience, es.readyState === EventSource.CONNECTING));

    // Everything the server publishes, by default: a new producer needs no front-end change to be seen.
    const watched: StreamTopic[] = topics ?? [...STREAM_TOPICS];
    const listeners: Array<[string, (event: MessageEvent) => void]> = [];
    for (const topic of watched) {
      const listener = (event: MessageEvent): void => {
        let payload: Record<string, unknown> = {};
        try {
          payload = JSON.parse(String(event.data)) as Record<string, unknown>;
        } catch {
          return;
        }
        const message: StreamMessage = { id: Number(event.lastEventId ?? 0), topic, payload };
        setLast(message);
        setCount((current) => current + 1);
        for (const handler of handlers.current.get(topic) ?? []) handler(message);
        onMessage.current?.(message);
      };
      es.addEventListener(topic, listener as EventListener);
      listeners.push([topic, listener]);
    }

    return () => {
      for (const [topic, listener] of listeners) es.removeEventListener(topic, listener as EventListener);
      if (poll !== null) clearInterval(poll);
      es.close();
      source.current = null;
      setState('closed');
    };
  }, [enabled, topics]);

  return { state, count, last, subscribe };
}
